import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'xemex-text-field',
  standalone: true,
  imports: [MatIconModule],
  templateUrl: './text-field.component.html',
  styleUrl: './text-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextFieldComponent),
      multi: true,
    },
  ],
  host: {
    '[class.invalid]': 'invalid()',
    '[class.disabled]': 'isDisabled()',
    '[class.readonly]': 'readonly()',
  },
})
export class TextFieldComponent implements ControlValueAccessor {
  label = input.required<string>();
  placeholder = input('');
  type = input('text');
  required = input(false);
  invalid = input(false);
  errorText = input('');
  autocomplete = input('off');
  inputId = input('');
  readonly = input(false);
  disabled = input(false);
  inputMode = input<string>('text');
  prefixIcon = input('');

  protected readonly controlDisabled = signal(false);
  protected readonly isDisabled = computed(() => this.disabled() || this.controlDisabled());

  // Password fields render a built-in show/hide toggle and swap the input type.
  protected readonly passwordVisible = signal(false);
  protected readonly isPassword = computed(() => this.type() === 'password');
  protected readonly effectiveType = computed(() =>
    this.isPassword() && this.passwordVisible() ? 'text' : this.type(),
  );

  protected readonly value = signal('');

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.value.set(value);
    this.onChange(value);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  protected togglePasswordVisibility(): void {
    this.passwordVisible.update(visible => !visible);
  }
}
